import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { DripDashboardSnapshot } from './types';
import { fetchDripDashboard } from './fetchDripDashboard';
import type { DripDataMode } from './fetchDripDashboard';
import { DripErrorBoundary } from './components/DripErrorBoundary';
import { DripToastProvider } from './components/DripToastProvider';
import { DripKpiStrip } from './components/DripKpiStrip';
import { DripAnalyticsCharts } from './components/DripAnalyticsCharts';
import { DripEngagementHeatmap } from './components/DripEngagementHeatmap';
import { DripCampaignsTable } from './components/DripCampaignsTable';
import {
  DripAbTestPanel,
  DripActivityFeed,
  DripAiRecommendations,
  DripAlertsBar,
  DripAudiencePanel,
  DripSettingsIntegrations,
} from './components/DripMarketingPanels';
import { DripTemplatesPanel } from './components/DripTemplatesPanel';
import { SITE_DRIP_TABBAR } from '../../constants/siteUi';

const DRIP_TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'templates', label: 'Templates' },
  { id: 'settings', label: 'Settings' },
] as const;

type DripTabId = (typeof DRIP_TABS)[number]['id'];

function normalizeDripTab(raw: string | null): DripTabId {
  if (raw === 'campaigns' || raw === 'settings' || raw === 'templates') return raw;
  return 'dashboard';
}

function DripTabBar({ activeTab }: { activeTab: DripTabId }) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const tabLinks = useMemo(() => {
    return DRIP_TABS.map((t) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', t.id);
      return { ...t, search: next.toString() };
    });
  }, [searchParams]);

  return (
    <div
      role="tablist"
      aria-label="Drip campaigns views"
      className={`flex w-full min-w-0 gap-1 overflow-x-auto p-1.5 ${SITE_DRIP_TABBAR}`}
    >
      {tabLinks.map((t) => {
        const selected = activeTab === t.id;
        return (
          <Link
            key={t.id}
            role="tab"
            aria-selected={selected}
            id={`drip-tab-${t.id}`}
            to={{ pathname, search: t.search }}
            className={`min-w-[5.5rem] flex-1 whitespace-nowrap rounded-xl px-3 py-2.5 text-center font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] transition sm:min-w-0 ${
              selected
                ? 'bg-white/[0.14] text-[var(--home-text-on-dark)] ring-1 ring-white/15'
                : 'text-[var(--home-muted-on-dark)] hover:bg-black/25 hover:text-[var(--home-text-on-dark)]'
            }`}
          >
            {t.label}
          </Link>
        );
      })}
    </div>
  );
}

function DripDashboardSkeleton() {
  return (
    <div className="animate-pulse space-y-6" aria-busy="true" aria-label="Loading drip dashboard">
      <div className="h-9 w-56 rounded-lg bg-white/[0.08]" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-white/[0.06]" />
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="h-72 rounded-2xl bg-white/[0.05]" />
        <div className="h-72 rounded-2xl bg-white/[0.05]" />
      </div>
      <div className="h-96 rounded-2xl bg-white/[0.05]" />
    </div>
  );
}

export function DripDashboardPage() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [searchParams] = useSearchParams();
  const activeTab = normalizeDripTab(searchParams.get('tab'));

  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [snapshot, setSnapshot] = useState<DripDashboardSnapshot | null>(null);
  const [mode, setMode] = useState<DripDataMode>('live');
  const [demoReason, setDemoReason] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setPhase('error');
      setErrorMessage('Sign in required.');
      return;
    }
    setPhase('loading');
    setErrorMessage(null);
    const res = await fetchDripDashboard(token);
    if (!res.ok) {
      setErrorMessage(res.message);
      setPhase('error');
      return;
    }
    setSnapshot(res.snapshot);
    setMode(res.mode);
    setDemoReason(res.demoReason);
    setPhase('ready');
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!token) {
    return <p className="text-sm text-[var(--home-muted-on-dark)]">Sign in to view drip campaigns.</p>;
  }

  if (phase === 'loading') {
    return <DripDashboardSkeleton />;
  }

  if (phase === 'error' || !snapshot) {
    return (
      <div className="rounded-2xl border border-rose-400/40 bg-rose-950/30 p-6 text-rose-100" role="alert">
        <p className="font-display text-sm font-semibold uppercase tracking-wide">Could not load dashboard</p>
        <p className="mt-2 text-sm">{errorMessage}</p>
        <button
          type="button"
          className="mt-4 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium hover:bg-white/20"
          onClick={() => void load()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <DripToastProvider>
      <div className="space-y-8 pb-16">
        <header className="space-y-2">
          <p className="font-mono text-[0.65rem] font-medium uppercase tracking-[0.22em] text-[var(--home-miami-blue)]">
            Marketing automation
          </p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[var(--home-text-on-dark)]">
            Drip campaigns
          </h1>
          <p className="max-w-3xl font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
            Enterprise-grade lifecycle messaging — connect your ESP to replace demo aggregates. Rate-limit aware writes,
            idempotent actions, and audit trails belong server-side.
          </p>
        </header>

        <DripTabBar activeTab={activeTab} />

        {mode === 'demo' ? (
          <div
            role="status"
            className="rounded-xl border border-amber-400/35 bg-amber-950/25 px-4 py-3 text-sm text-amber-100 ring-1 ring-amber-400/15"
          >
            <strong className="font-semibold">Sample data</strong>
            <span className="text-amber-100/85">
              {' '}
              —{' '}
              {demoReason === 'fixtures_env'
                ? '`VITE_DRIP_USE_FIXTURES=true` is set.'
                : demoReason === 'api_501'
                  ? 'Live API returned 501 (not implemented).'
                  : 'Demo dataset active.'}
            </span>
          </div>
        ) : null}

        {activeTab === 'dashboard' ? (
          <div
            role="tabpanel"
            id="drip-tabpanel-dashboard"
            aria-labelledby="drip-tab-dashboard"
            className="space-y-8"
          >
            <section id="drip-kpis" className="scroll-mt-32">
              <DripKpiStrip kpis={snapshot.kpis} />
            </section>

            <DripErrorBoundary onReset={load}>
              <DripAnalyticsCharts
                series={snapshot.series}
                funnel={snapshot.funnel}
                devices={snapshot.devices}
                topCampaigns={snapshot.topCampaigns}
              />
            </DripErrorBoundary>

            <DripErrorBoundary onReset={load}>
              <DripEngagementHeatmap heatmap={snapshot.heatmap} />
            </DripErrorBoundary>

            <div id="drip-automation-insights" className="scroll-mt-32 grid gap-6 xl:grid-cols-3">
              <div className="space-y-6 xl:col-span-2">
                <DripAlertsBar alerts={snapshot.alerts} />
                <DripAbTestPanel abTests={snapshot.abTests} />
              </div>
              <div className="space-y-6">
                <DripAudiencePanel segmentRules={snapshot.segmentRules} />
                <DripActivityFeed activities={snapshot.activities} />
                <DripAiRecommendations aiRecommendations={snapshot.aiRecommendations} />
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'campaigns' ? (
          <div
            role="tabpanel"
            id="drip-tabpanel-campaigns"
            aria-labelledby="drip-tab-campaigns"
            className="space-y-6"
          >
            <DripCampaignsTable rows={snapshot.campaigns} />
          </div>
        ) : null}

        {activeTab === 'settings' ? (
          <div role="tabpanel" id="drip-tabpanel-settings" aria-labelledby="drip-tab-settings" className="space-y-6">
            <DripSettingsIntegrations integrations={snapshot.integrations} />
          </div>
        ) : null}

        {activeTab === 'templates' ? (
          <div
            role="tabpanel"
            id="drip-tabpanel-templates"
            aria-labelledby="drip-tab-templates"
            className="space-y-6"
          >
            <DripTemplatesPanel />
          </div>
        ) : null}
      </div>
    </DripToastProvider>
  );
}
